import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";

const companyApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    createCompany: build.mutation<Company, CreateCompanyRequest>({
      query: (request) => ({
        url: `/companies/company`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["Company"],
    }),
    patchCompany: build.mutation<Company, PatchCompanyRequest>({
      query: (request) => ({
        url: `/companies/company`,
        method: "PATCH",
        body: request,
      }),
      // On success only the list is invalidated, so the name shown in the
      // list and the sidebar refreshes without refetching the entry the
      // user is typing in. On error the entry is invalidated, which resets
      // the optimistic cache value to the server state.
      invalidatesTags: (_result, error, request) =>
        error
          ? [{ type: "Company", id: request.id }]
          : [{ type: "Company", id: "LIST" }],
    }),
    showCompany: build.query<Company, ShowCompanyRequest>({
      query: (request) => ({
        url: `/companies/company`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "Company", id: result.id }] : [],
    }),
    listCompany: build.query<CompanyList, ListCompanyRequest>({
      query: (request) => ({
        url: `/companies/company/list`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.companies.map((company) => ({
                type: "Company" as const,
                id: company.id,
              })),
              { type: "Company", id: "LIST" },
            ]
          : [{ type: "Company", id: "LIST" }],
    }),
    deleteCompany: build.mutation<Company, DeleteCompanyRequest>({
      query: (request) => ({
        url: `/companies/company`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["Company"],
    }),
  }),
});

export const updateCompanyCache = updateCache(companyApi, "showCompany");

export const {
  useCreateCompanyMutation,
  usePatchCompanyMutation,
  useShowCompanyQuery,
  useListCompanyQuery,
  useDeleteCompanyMutation,
} = companyApi;

export interface Company {
  id: string;
  name: string;
  shortName: string;
}

export interface CompanyList {
  companies: Company[];
  count: number;
}

export function sortCompanies(companies: Company[], sortBy: string) {
  const sortable: Company[] = [];
  companies.forEach((company) => {
    sortable.push(company);
  });
  const compareFn = (a: Company, b: Company) => {
    switch (sortBy) {
      case "name":
        return a.name.localeCompare(b.name);
      case "shortName":
        return a.shortName.localeCompare(b.shortName);
      default:
        return a.name.localeCompare(b.name);
    }
  };
  return sortable.sort(compareFn);
}

export interface CreateCompanyRequest {
  id: string;
  name: string;
  shortName: string;
}

export interface PatchCompanyRequest {
  id: string;
  name?: string;
  shortName?: string;
}

export interface DeleteCompanyRequest {
  id: string;
}

export interface ShowCompanyRequest {
  id: string;
}

export type ListCompanyRequest = object;
